import React, { forwardRef } from 'react';
import styles from './Input.module.css';

const Input = forwardRef(({
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  helperText,
  size = 'medium',
  fullWidth = false,
  disabled = false,
  required = false,
  icon,
  iconPosition = 'left',
  className = '',
  inputClassName = '',
  id,
  name,
  autoComplete,
  ariaLabel,
  ariaDescribedBy,
  ...props
}, ref) => {
  const inputId = id || name || `input-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;
  
  const wrapperClassNames = [
    styles.wrapper,
    fullWidth && styles.fullWidth,
    className
  ].filter(Boolean).join(' ');
  
  const inputWrapperClassNames = [
    styles.inputWrapper,
    styles[size],
    error && styles.error,
    disabled && styles.disabled,
    icon && styles.withIcon,
    icon && styles[`icon-${iconPosition}`]
  ].filter(Boolean).join(' ');
  
  const inputClassNames = [
    styles.input,
    inputClassName
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClassNames}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required} aria-label="required">*</span>}
        </label>
      )}
      
      <div className={inputWrapperClassNames}>
        {icon && iconPosition === 'left' && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          className={inputClassNames}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          aria-label={ariaLabel || label}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={[
            error && errorId,
            helperText && helperId,
            ariaDescribedBy
          ].filter(Boolean).join(' ')}
          {...props}
        />
        
        {icon && iconPosition === 'right' && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
      </div>
      
      {error && (
        <p id={errorId} className={styles.errorText} role="alert">
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p id={helperId} className={styles.helperText}>
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input; 